import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { friendsApi } from '../lib/api';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Users, UserPlus, Search, MessageSquare, Check, X, Trash2, ArrowLeft,
  RefreshCw, Clock, UserCheck,
} from 'lucide-react';
import PrivateChatDialog from './friends/PrivateChatDialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { usePresence } from '../contexts/PresenceContext';

const isItalianFn = () => (typeof window !== 'undefined' && window.localStorage?.getItem('lang') !== 'en');

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'ora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}g`;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

export default function Friends() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isItalian = isItalianFn();
  const initialTab = ['friends', 'requests', 'search'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'friends';
  const [tab, setTab] = useState(initialTab);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openFriendshipId, setOpenFriendshipId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [fl, rq] = await Promise.all([friendsApi.list(), friendsApi.requests()]);
      setFriends(Array.isArray(fl.data) ? fl.data : []);
      setRequests(rq.data || { incoming: [], outgoing: [] });
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Sync tab when URL ?tab= changes (e.g. via notification click while on the page)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['friends', 'requests', 'search'].includes(t)) setTab(t);
    const openId = searchParams.get('open');
    if (openId) setOpenFriendshipId(openId);
  }, [searchParams]);

  // Refresh when chat closes
  useEffect(() => {
    if (openFriendshipId === null) refresh();
  }, [openFriendshipId, refresh]);

  // Live search debounce
  useEffect(() => {
    if (tab !== 'search') return;
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await friendsApi.search(q);
        setSearchResults(res.data || []);
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery, tab]);

  const sendRequest = async (recipientId) => {
    try {
      await friendsApi.request(recipientId);
      toast.success(isItalian ? 'Richiesta inviata' : 'Request sent');
      // Refresh search results state
      setSearchResults(prev => prev.map(u => u.user_id === recipientId
        ? { ...u, friendship: { status: 'pending', requester_id: user?.user_id } }
        : u));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const acceptRequest = async (fid) => {
    try {
      await friendsApi.accept(fid);
      toast.success(isItalian ? 'Amicizia accettata' : 'Friendship accepted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const rejectRequest = async (fid) => {
    try {
      await friendsApi.reject(fid);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const removeFriend = async (fid, gamertag) => {
    if (!window.confirm(isItalian
      ? `Rimuovere ${gamertag} dagli amici?`
      : `Remove ${gamertag} from friends?`)) return;
    try {
      await friendsApi.remove(fid);
      toast.success(isItalian ? 'Amicizia rimossa' : 'Friendship removed');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const incomingCount = requests.incoming?.length || 0;

  return (
    <div className="min-h-screen ept-bg-ambient text-white pb-12">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white" data-testid="friends-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-heading text-2xl uppercase tracking-wider flex items-center gap-2">
                <Users size={20} className="text-[#00F0FF]" />
                {isItalian ? 'Amici' : 'Friends'}
              </h1>
              <p className="text-xs text-slate-500 mt-1">
                {isItalian ? 'Gestisci amici e chat private' : 'Manage friends and private chats'}
              </p>
            </div>
          </div>
          <button onClick={refresh} className="text-slate-400 hover:text-white p-2" data-testid="friends-refresh-btn">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-4">
          <TabButton active={tab === 'friends'} onClick={() => setTab('friends')} icon={Users} label={isItalian ? 'Amici' : 'Friends'} count={friends.length} testId="tab-friends" />
          <TabButton active={tab === 'requests'} onClick={() => setTab('requests')} icon={UserCheck} label={isItalian ? 'Richieste' : 'Requests'} count={incomingCount} highlight={incomingCount > 0} testId="tab-requests" />
          <TabButton active={tab === 'search'} onClick={() => setTab('search')} icon={Search} label={isItalian ? 'Cerca' : 'Search'} testId="tab-search" />
        </div>

        {/* FRIENDS TAB */}
        {tab === 'friends' && (
          <div className="space-y-2" data-testid="friends-list">
            {friends.length === 0 ? (
              <EmptyState icon={Users} text={isItalian ? 'Nessun amico ancora. Cerca utenti per iniziare!' : 'No friends yet. Search users to start!'} />
            ) : friends.map(f => (
              <FriendCard
                key={f.friendship_id}
                friend={f.friend}
                lastMessage={f.last_message}
                unread={f.unread_count}
                onChat={() => setOpenFriendshipId(f.friendship_id)}
                onRemove={() => removeFriend(f.friendship_id, f.friend?.gamertag || f.friend?.name)}
                isItalian={isItalian}
              />
            ))}
          </div>
        )}

        {/* REQUESTS TAB */}
        {tab === 'requests' && (
          <div className="space-y-4" data-testid="requests-list">
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                {isItalian ? `In arrivo (${incomingCount})` : `Incoming (${incomingCount})`}
              </h3>
              {requests.incoming?.length === 0 ? (
                <p className="text-xs text-slate-600 italic">{isItalian ? 'Nessuna richiesta in arrivo' : 'No incoming requests'}</p>
              ) : requests.incoming.map(r => (
                <RequestCard key={r.friendship_id} req={r} type="incoming"
                  onAccept={() => acceptRequest(r.friendship_id)}
                  onReject={() => rejectRequest(r.friendship_id)}
                  isItalian={isItalian}
                />
              ))}
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                {isItalian ? `Inviate (${requests.outgoing?.length || 0})` : `Outgoing (${requests.outgoing?.length || 0})`}
              </h3>
              {requests.outgoing?.length === 0 ? (
                <p className="text-xs text-slate-600 italic">{isItalian ? 'Nessuna richiesta inviata' : 'No outgoing requests'}</p>
              ) : requests.outgoing.map(r => (
                <RequestCard key={r.friendship_id} req={r} type="outgoing"
                  onCancel={() => rejectRequest(r.friendship_id)}
                  isItalian={isItalian}
                />
              ))}
            </div>
          </div>
        )}

        {/* SEARCH TAB */}
        {tab === 'search' && (
          <div className="space-y-3" data-testid="search-tab">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isItalian ? 'Cerca per gamertag o nome…' : 'Search by gamertag or name…'}
                className="pl-10 bg-[#0a0d15] border-white/10 text-white"
                data-testid="user-search-input"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              {searchQuery.trim().length < 2 ? (
                <p className="text-xs text-slate-600 italic text-center py-6">
                  {isItalian ? 'Inserisci almeno 2 caratteri' : 'Type at least 2 characters'}
                </p>
              ) : searching ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin w-5 h-5 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-xs text-slate-600 italic text-center py-6">
                  {isItalian ? 'Nessun utente trovato' : 'No user found'}
                </p>
              ) : searchResults.map(u => (
                <SearchResultCard
                  key={u.user_id}
                  u={u}
                  isItalian={isItalian}
                  onSendRequest={() => sendRequest(u.user_id)}
                  onAccept={() => acceptRequest(u.friendship?.friendship_id)}
                  onChat={() => setOpenFriendshipId(u.friendship?.friendship_id)}
                />
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {openFriendshipId && (
        <PrivateChatDialog
          open={!!openFriendshipId}
          onOpenChange={(o) => { if (!o) setOpenFriendshipId(null); }}
          friendshipId={openFriendshipId}
          isItalian={isItalian}
          currentUserId={user?.user_id}
        />
      )}
    </div>
  );
}

const TabButton = ({ active, onClick, icon: Icon, label, count, highlight, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors ${
      active ? 'border-[#00F0FF] text-[#00F0FF]' : 'border-transparent text-slate-400 hover:text-white'
    }`}
    data-testid={testId}
  >
    <Icon size={14} />
    <span>{label}</span>
    {count > 0 && (
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
        highlight ? 'bg-[#FF1744] text-white' : 'bg-white/10 text-slate-300'
      }`}>{count}</span>
    )}
  </button>
);

const Avatar = ({ user, size = 36, online }) => (
  <div className="relative shrink-0">
    <div className="rounded-full overflow-hidden bg-[#050508] border border-white/10 flex items-center justify-center"
      style={{ width: size, height: size }}>
      {user?.picture
        ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
        : <Users size={size * 0.5} className="text-slate-500" />}
    </div>
    {online && (
      <span className="absolute bottom-0 right-0 block w-3 h-3 rounded-full bg-[#00E676] border-2 border-[#0a0d15]" data-testid="online-dot" />
    )}
  </div>
);

const FriendCard = ({ friend, lastMessage, unread, onChat, onRemove, isItalian }) => {
  const { isOnline } = usePresence();
  const online = isOnline(friend?.user_id);
  return (
  <div className={`bg-[#0a0d15] border rounded p-3 flex items-center gap-3 hover:bg-white/5 transition-colors ${
    unread > 0 ? 'border-[#00F0FF]/40' : 'border-white/10'
  }`} data-testid={`friend-card-${friend?.user_id}`}>
    <Avatar user={friend} online={online} />
    <div className="flex-1 min-w-0 cursor-pointer" onClick={onChat}>
      <div className="flex items-center gap-2">
        <span className="font-bold text-sm truncate">{friend?.gamertag || friend?.name}</span>
        {online && <span className="text-[10px] text-[#00E676]">{isItalian ? 'online' : 'online'}</span>}
      </div>
      {lastMessage ? (
        <div className="text-xs text-slate-400 truncate">
          <span className="text-slate-600">{lastMessage.sender_id === friend?.user_id ? '' : (isItalian ? 'Tu: ' : 'You: ')}</span>
          {lastMessage.content}
        </div>
      ) : (
        <div className="text-xs text-slate-600 italic">{isItalian ? 'Inizia la conversazione' : 'Start chatting'}</div>
      )}
    </div>
    {lastMessage && (
      <span className="text-[10px] text-slate-500 shrink-0">{formatRelative(lastMessage.created_at)}</span>
    )}
    {unread > 0 && (
      <span className="bg-[#00F0FF] text-black text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
        {unread > 99 ? '99+' : unread}
      </span>
    )}
    <Button size="icon" variant="ghost" onClick={onChat} className="text-[#00F0FF] hover:bg-[#00F0FF]/10 h-8 w-8" data-testid={`friend-chat-${friend?.user_id}`}>
      <MessageSquare size={14} />
    </Button>
    <Button size="icon" variant="ghost" onClick={onRemove} className="text-slate-500 hover:text-[#FF1744] hover:bg-[#FF1744]/10 h-8 w-8" data-testid={`friend-remove-${friend?.user_id}`}>
      <Trash2 size={14} />
    </Button>
  </div>
  );
};

const RequestCard = ({ req, type, onAccept, onReject, onCancel, isItalian }) => (
  <div className="bg-[#0a0d15] border border-white/10 rounded p-3 flex items-center gap-3 mb-2"
    data-testid={`request-${type}-${req.friendship_id}`}>
    <Avatar user={req.user} />
    <div className="flex-1 min-w-0">
      <div className="font-bold text-sm truncate">{req.user?.gamertag || req.user?.name}</div>
      <div className="text-[11px] text-slate-500 flex items-center gap-1">
        <Clock size={10} /> {formatRelative(req.created_at)}
      </div>
    </div>
    {type === 'incoming' ? (
      <>
        <Button size="sm" onClick={onAccept} className="bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30 hover:bg-[#00E676]/30" data-testid={`accept-${req.friendship_id}`}>
          <Check size={14} className="mr-1" /> {isItalian ? 'Accetta' : 'Accept'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onReject} className="text-slate-400 hover:text-[#FF1744]" data-testid={`reject-${req.friendship_id}`}>
          <X size={14} />
        </Button>
      </>
    ) : (
      <>
        <span className="text-xs text-slate-500 px-2">{isItalian ? 'In attesa' : 'Pending'}</span>
        <Button size="sm" variant="ghost" onClick={onCancel} className="text-slate-400 hover:text-[#FF1744]" data-testid={`cancel-${req.friendship_id}`}>
          <X size={14} />
        </Button>
      </>
    )}
  </div>
);

const SearchResultCard = ({ u, isItalian, onSendRequest, onAccept, onChat }) => {
  const f = u.friendship;
  return (
    <div className="bg-[#0a0d15] border border-white/10 rounded p-3 flex items-center gap-3" data-testid={`search-result-${u.user_id}`}>
      <Avatar user={u} />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{u.gamertag || u.name}</div>
        {u.gamertag && u.name && <div className="text-[11px] text-slate-500 truncate">{u.name}</div>}
      </div>
      {!f && (
        <Button size="sm" onClick={onSendRequest} className="bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/30" data-testid={`send-request-${u.user_id}`}>
          <UserPlus size={14} className="mr-1" /> {isItalian ? 'Aggiungi' : 'Add'}
        </Button>
      )}
      {f?.status === 'pending' && f.requester_id === u.user_id && (
        <Button size="sm" onClick={onAccept} className="bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
          <Check size={14} className="mr-1" /> {isItalian ? 'Accetta' : 'Accept'}
        </Button>
      )}
      {f?.status === 'pending' && f.requester_id !== u.user_id && (
        <span className="text-xs text-slate-500 px-2">{isItalian ? 'Richiesta inviata' : 'Request sent'}</span>
      )}
      {f?.status === 'accepted' && (
        <Button size="sm" onClick={onChat} className="bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30">
          <MessageSquare size={14} className="mr-1" /> Chat
        </Button>
      )}
    </div>
  );
};

const EmptyState = ({ icon: Icon, text }) => (
  <div className="text-center py-16 bg-[#0a0d15] border border-white/10 rounded">
    <Icon size={32} className="text-slate-600 mx-auto mb-3" />
    <p className="text-slate-400 text-sm">{text}</p>
  </div>
);
